import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Customer, CustomerList } from '../types/customer';
import { toast } from 'react-toastify';
import CustomerDialog from '../components/CustomerDialog';

const CustomersPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);

  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCustomer, setMenuCustomer] = useState<Customer | null>(null);

  const isMenuOpen = Boolean(anchorEl);
  const canManageCustomers = user?.role === 'super_admin';

  useEffect(() => {
    loadCustomers();
  }, [page, rowsPerPage, search]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response: CustomerList = await apiService.getCustomers(
        page * rowsPerPage,
        rowsPerPage,
        search || undefined
      );
      setCustomers(response.customers);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error(t('customers.loadCustomersError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchValue);
    setPage(0);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget);
    setMenuCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCustomer(null);
  };

  const handleCreateCustomer = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      await apiService.deleteCustomer(customerToDelete.id);
      toast.success(t('customers.customerDeleted'));
      loadCustomers();
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('customers.customerDeleteError'));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleCustomerSaved = () => {
    loadCustomers();
    handleDialogClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getCommunicationChannelLabel = (channel: string) => {
    return t(`customers.communicationChannels.${channel}`, { defaultValue: channel });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('customers.title')}
        </Typography>
        {canManageCustomers && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCustomer}
          >
            {t('customers.newCustomer')}
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder={t('customers.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="contained" onClick={handleSearch}>
            {t('common.search')}
          </Button>
          {search && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                setSearchValue('');
                setPage(0);
              }}
            >
              {t('customers.clearSearch')}
            </Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('customers.table.customer')}</TableCell>
              <TableCell>{t('customers.table.countryRegion')}</TableCell>
              <TableCell>{t('customers.table.businessContact')}</TableCell>
              <TableCell>{t('customers.table.technicalContact')}</TableCell>
              <TableCell>{t('customers.table.mainChannel')}</TableCell>
              <TableCell>{t('customers.table.tenants')}</TableCell>
              <TableCell>{t('customers.table.onboardingDate')}</TableCell>
              <TableCell align="center">{t('customers.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search ? t('customers.noCustomersFound') : t('customers.noCustomers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {customer.name}
                        </Typography>
                        {customer.platform_objective && (
                          <Typography variant="caption" color="text.secondary">
                            {customer.platform_objective}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{customer.country_region}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {customer.business_owner_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {customer.business_owner_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {customer.technical_contact_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {customer.technical_contact_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getCommunicationChannelLabel(customer.main_communication_channel)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={customer.tenant_count || 0}
                      size="small"
                      color={(customer.tenant_count || 0) > 0 ? 'primary' : 'default'}
                      clickable={(customer.tenant_count || 0) > 0}
                      onClick={() => {
                        if ((customer.tenant_count || 0) > 0) {
                          window.location.href = `/tenants?customer=${customer.id}`;
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(customer.onboarding_date)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {canManageCustomers && (
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, customer)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => menuCustomer && handleEditCustomer(menuCustomer)}>
          <EditIcon sx={{ mr: 1 }} />
          {t('common.edit')}
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuCustomer) {
            // Navigate to tenants page with customer filter
            window.location.href = `/tenants?customer=${menuCustomer.id}`;
          }
          handleMenuClose();
        }}>
          <BusinessIcon sx={{ mr: 1 }} />
          Ver Tenants ({menuCustomer?.tenant_count || 0})
        </MenuItem>
        <MenuItem 
          onClick={() => menuCustomer && handleDeleteCustomer(menuCustomer)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          {t('common.delete')}
        </MenuItem>
      </Menu>

      {/* Customer Dialog */}
      <CustomerDialog
        open={dialogOpen}
        customer={selectedCustomer}
        onClose={handleDialogClose}
        onSave={handleCustomerSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('customers.confirmDelete')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t('customers.deleteMessage', { name: customerToDelete?.name })}
          </Typography>
          {(customerToDelete?.tenant_count || 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('customers.deleteWarning', { count: customerToDelete?.tenant_count || 0 })}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={confirmDelete}
              disabled={(customerToDelete?.tenant_count || 0) > 0}
            >
              {t('common.delete')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CustomersPage;